import { createFileRoute, Navigate } from "@tanstack/react-router";
import { Box, Separator, Text, useIsMobile } from "@salestrak/ui";

import { PageHeader } from "#app/components/page-header";
import { useWorkspace } from "#app/lib/data/workspace-context";

// TanStack has no built-in Unmatched screen — render a minimal not-found body.
function Unmatched() {
  return (
    <Box fill="flex" align="center" justify="center">
      <Text variant="muted">This page could not be found.</Text>
    </Box>
  );
}

// Catch-all for any path under a workspace that didn't match a real route.
// Its only job is case-recovery: route *names* are case-sensitive, so
// `/sales/Map` 404s even though `/sales/map` exists. We re-case each segment
// that matches a known static route name and redirect to the canonical path;
// genuinely unknown paths fall through to the normal Unmatched screen.
//
// Critical: dynamic id segments ($workflowId, $apiKeyId, $teamId, …) are
// case-sensitive and must survive untouched — `/sales/workflow/AbC123/Board`
// must keep `AbC123` and only fix `Board`→`board`. We achieve that by
// normalizing ONLY segments whose lowercase form is in STATIC_SEGMENTS; an
// id never appears here, so it's passed through byte-for-byte. (Omitting a
// route name here just means that one route won't be case-corrected — never
// an id corruption. Add new static route segments as routes are added.)
const STATIC_SEGMENTS = new Set([
  // top-level workspace sections
  "map",
  "inbox",
  "members",
  "teams",
  "settings",
  "workflow",
  "team",
  "member",
  "my-work",
  // workflow/$workflowId + team/$teamId view tabs
  "overview",
  "board",
  "list",
  "dashboard",
  // settings subtree
  "access",
  "api",
  "keys",
  "webhooks",
  "custom-fields",
  "issue-types",
  "preferences",
  "profile",
  "reasons",
  "trash",
  "updates",
  "workflows",
  "stages",
  // shared leaf actions
  "create",
  "edit",
]);

function WorkspaceCatchAll() {
  const { workspace, _splat } = Route.useParams();
  const isMobile = useIsMobile();
  const { activeWorkspace } = useWorkspace();
  const rest = (_splat ?? "").split("/").filter(Boolean);

  const normalized = rest.map((seg) => {
    const lower = seg.toLowerCase();
    return STATIC_SEGMENTS.has(lower) ? lower : seg;
  });

  const changed = normalized.some((seg, i) => seg !== rest[i]);

  if (changed) {
    // Re-attach the query string explicitly — the map route depends on
    // ?lat&lng&z.
    const search = typeof window !== "undefined" ? window.location.search : "";
    const path = [workspace, ...normalized].join("/");
    return <Navigate to={`/${path}${search}` as unknown as "/"} />;
  }

  // Desktop keeps the bare Unmatched screen — the rail and module menu stay
  // visible alongside it.
  if (!isMobile) return <Unmatched />;

  // Mobile: the drawer is the only path back to navigation and Unmatched
  // renders no chrome, so the user would be stranded. Give it the Home-style
  // header with the workspace name — its breadcrumb carries the mobile nav
  // trigger that reopens the drawer.
  return (
    <Box direction="column" fill="flex">
      <PageHeader title={activeWorkspace?.name ?? "Workspace"} />
      <Separator />
      <Unmatched />
    </Box>
  );
}

export const Route = createFileRoute("/(app)/$workspace/$")({
  component: WorkspaceCatchAll,
});
